import moment from 'moment'
import Vue from 'vue'
import VueCookies from 'vue-cookies'

import Vidle from 'v-idle'
import VueCookieAcceptDecline from 'vue-cookie-accept-decline'
import 'vue-cookie-accept-decline/dist/vue-cookie-accept-decline.css'
import Hotjar from 'vue-hotjar'
import App from './App.vue'
import createDynamicTheme from './common/theme'
import eventBus from './event-bus.js'
import eventTypes from './eventTypes'
import i18n from './i18n'
import vuetify from './plugins/vuetify'
import createRouter from './router'
import server from './services/api/server'
import users from './services/api/users'
import createAuth from './services/auth/auth.js'
import createPopupAuth from './services/auth/popupAuth.js'
import signatureChainUtils from './signatueChainUtils'
import store from './store'
import CommonError from './views/CommonError'
import RdssError from './views/RdssError'
import rsspUtil from './services/api/rsspUtil'

Vue.config.productionTip = false
// todo temporary console cleanup
// Vue.config.silent = true
Vue.use(VueCookies)
Vue.use(Vidle)
Vue.use(VueCookieAcceptDecline)

Vue.mixin(signatureChainUtils)
Vue.mixin({
  methods: {
    // https://stackoverflow.com/questions/62462276/how-to-solve-avoided-redundant-navigation-to-current-location-error-in-vue
    /**
     * Avoids redundand error when navigating to already active page
     */
    routerPush(route) {
      this.$router.push(route).catch((error) => {
        if (error.name !== 'NavigationDuplicated') {
          throw error
        }
      })
    },
    routerReplace(route) {
      this.$router.replace(route).catch((error) => {
        if (error.name !== 'NavigationDuplicated') {
          throw error
        }
      })
    },
    convertDate(value) {
      if (value == null) {
        return ' - '
      }

      return moment(value).local().format()
    },
    convertDateEmpty(value) {
      if (value == null) {
        return ''
      }

      return moment(value).local().format()
    },

    formatDate(dateString) {
      if (!dateString) return null
      const date = new Date(dateString)
      const locale = this.$i18n?.locale || navigator.language || 'en'
      return date.toLocaleString(locale, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    formatDuration(ms) {
      if (!ms) return this.$t('trustx.duration.zero')
      const totalSeconds = Math.floor(ms / 1000)
      const minutes = Math.floor(totalSeconds / 60)
      const seconds = totalSeconds % 60
      return this.$t('trustx.duration.format', {
        minutes,
        seconds: String(seconds).padStart(2, '0')
      })
    },

    setLanguage(locale, callrefresh = true) {
      this.$i18n.locale = locale // Set the new locale
      this.$vuetify.lang.current = locale // update Vuetify's language setting
      if (callrefresh) {
        window.location.reload() // dirty hack to refresh the navigation menu localisation too
      }
    },

    getErrorMessage(error) {
      let errorMsg = this.$t('toastMessage.errorWhileRetrievingData')
      if (error.response !== undefined) {
        console.error('error.response', error.response)
      } else {
        console.error('error', error)
      }
      let errorCode
      let responseErrorMsg

      if ((error.response !== undefined) && (error.response.data.code !== undefined)) {
        errorCode = error.response.data.code
        responseErrorMsg = error.response.data.message
      } else if ((error.response !== undefined) && (error.response.data.error !== undefined)) {
        if (error.response.data.error === 'general') {
          errorCode = CommonError.GENERAL
          responseErrorMsg = error.response.data.error_description
        }
      }

      if ((error.response !== undefined) && (errorCode !== undefined)) {
        const errorcodeUpped = errorCode.toUpperCase()
        switch (errorcodeUpped) {
          case RdssError.SIGNING_CERTIFICATE_IS_NOT_APPROPRIATE:
            errorMsg = this.$t('backendErrors.SIGNING_CERTIFICATE_IS_NOT_APPROPRIATE')
            break
          case RdssError.INCORRECT_KEYPASSWORD_OR_PUK:
            errorMsg = this.$t('backendErrors.INCORRECT_KEYPASSWORD_OR_PUK')
            break
          case RdssError.CREDENTIAL_NOT_ACTIVATED:
            errorMsg = this.$t('backendErrors.CREDENTIAL_NOT_ACTIVATED')
            break
          case RdssError.EMPTY_CERTIFICATE_CHAIN:
            errorMsg = this.$t('backendErrors.EMPTY_CERTIFICATE_CHAIN')
            break
          case RdssError.KEY_BLOCKED:
            errorMsg = this.$t('backendErrors.KEY_BLOCKED')
            break
          case RdssError.DOCUMENT_PARSING_ERROR:
            errorMsg = this.$t('backendErrors.DOCUMENT_PARSING_ERROR')
            break
          case RdssError.DOCUMENT_CANNOT_BE_MODIFIED:
            errorMsg = this.$t('backendErrors.DOCUMENT_CANNOT_BE_MODIFIED')
            break
          case RdssError.USER_SUSPENDED:
            this.$router.push('/permissionDenied')
            break
          case RdssError.EVRO_CERTIFICATE_NOT_FOUND:
            errorMsg = this.$t('backendErrors.EVRO_CERTIFICATE_NOT_FOUND')
            break
          case RdssError.EVRO_CERTIFICATE_EXPIRED:
            errorMsg = this.$t('backendErrors.EVRO_CERTIFICATE_EXPIRED')
            break
          case RdssError.EVRO_USER_NOT_FOUND:
            errorMsg = this.$t('backendErrors.EVRO_USER_NOT_FOUND')
            break
          case RdssError.EVRO_OCCASIONAL_INVALID_SIGNATURE:
            errorMsg = this.$t('backendErrors.EVRO_OCCASIONAL_INVALID_SIGNATURE')
            break
          case RdssError.SIGNATURE_CHAIN_CONCURRENT_MODIFICATION:
            errorMsg = this.$t('backendErrors.SIGNATURE_CHAIN_CONCURRENT_MODIFICATION')
            break
          case RdssError.SIGNING_TRAIL_NOT_SUPPORTED_FOR_PASSWORD_PROTECTED_PDF:
            errorMsg = this.$t('backendErrors.SIGNING_TRAIL_NOT_SUPPORTED_FOR_PASSWORD_PROTECTED_PDF', { fileName: error.fileName })
            break
          case RdssError.NO_SHARED_BUSINESS_ACCOUNT:
            errorMsg = this.$t('backendErrors.NO_SHARED_BUSINESS_ACCOUNT')
            break
          case RdssError.NO_SHARED_USER_GROUP:
            errorMsg = this.$t('backendErrors.NO_SHARED_USER_GROUP')
            break
          case RdssError.LIMIT_EXCEEDED: {
            const details = error?.response?.data?.detail
            if (
              !details ||
              details.requested === undefined ||
              details.maxAllowed === undefined
            ) {
              errorMsg = this.$t('backendErrors.LIMIT_EXCEEDED')
            } else {
              errorMsg = this.$t('backendErrors.LIMIT_EXCEEDED_PARAMS', {
                requested: details.requested,
                maxAllowed: details.maxAllowed
              })
            }
            break
          }
          case RdssError.PROVIDER_CONFIG_LIMIT_EXCEEDED:
            errorMsg = this.$t('backendErrors.PROVIDER_CONFIG_LIMIT_EXCEEDED')
            break
          case CommonError.ALREADY_EXISTS:
            errorMsg = this.$t('toastMessage.alreadyExist', { 0: error.response.data.message.split(' ')[0] })
            break
          case CommonError.STILL_REFERENCED:
            // eslint-disable-next-line no-case-declarations
            const splittedErrorDescription = error.response.data.message.split(' ')
            errorMsg = this.$t('toastMessage.stillReferenced', { 0: splittedErrorDescription[0], 1: splittedErrorDescription[5] })
            break
          case CommonError.GENERAL:
          case CommonError.BAD_REQUEST:
          case CommonError.BAD_REQUEST2:
          case CommonError.INVALID_REQUEST:
          case CommonError.DEFAULT_INVALID_REQUEST:
          case CommonError.VALIDATION_FAILED:
          case CommonError.INVALID_JSON:
          case CommonError.FORBIDDEN:
          case CommonError.NOT_FOUND:
            errorMsg = responseErrorMsg !== undefined ? this.$t('backendErrors.CLIENT_ERROR', { message: responseErrorMsg }) : this.$t('backendErrors.CLIENT_ERROR_NO_MSG') +
              ' ' + this.$t('backendErrors.ADDITIONAL_DETAILS', { datetime: new Date().toUTCString(), code: errorcodeUpped })
            break
          default:
            errorMsg = this.$t('backendErrors.SERVER_ERROR') + ' ' + this.$t('backendErrors.ADDITIONAL_DETAILS', { datetime: new Date().toUTCString(), code: errorcodeUpped })
        }
      } else {
        errorMsg = this.getNotHandledErrorMessage(error)
      }
      return errorMsg
    },
    getNotHandledErrorMessage(error) {
      if (error.response !== undefined) {
        if (error.response.status === 500) {
          // return this.$t('backendErrors.SERVER_ERROR') + this.$t('backendErrors.ADDITIONAL_DETAILS', { datetime: new Date().toUTCString(), code: error.response.status })
          return this.$t('backendErrors.SERVER_ERROR') + ' ' + this.$t('backendErrors.ADDITIONAL_DETAILS', { datetime: new Date().toUTCString(), code: error.response.status })
        } else {
          return this.$t('backendErrors.SERVER_ERROR') + ' ' + this.$t('backendErrors.ADDITIONAL_DETAILS', { datetime: new Date().toUTCString(), code: error.response.status })
        }
      } else {
        // timeout error
        if (error.code === 'ECONNABORTED') {
          return this.$t('errors.timeOut') + ' ' + this.$t('errors.additionalDetails', { datetime: new Date().toUTCString(), msg: error.message })
        } else {
          return this.$t('backendErrors.SERVER_ERROR') + ' ' + this.$t('backendErrors.ADDITIONAL_DETAILS', { datetime: new Date().toUTCString(), code: error.code })
        }
      }
    },

    adjustColor(hex, percent) {
      if (!hex) return hex

      const num = parseInt(hex.replace('#', ''), 16)
      const amt = Math.round(2.55 * percent)

      const R = (num >> 16) + amt
      const G = ((num >> 8) & 0x00FF) + amt
      const B = (num & 0x0000FF) + amt

      return (
        '#' +
    (
      0x1000000 +
      (R < 255 ? (R < 0 ? 0 : R) : 255) * 0x10000 +
      (G < 255 ? (G < 0 ? 0 : G) : 255) * 0x100 +
      (B < 255 ? (B < 0 ? 0 : B) : 255)
    )
      .toString(16)
      .slice(1)
      )
    },
    updateCustomCssVars(themeColors, customVars = []) {
      customVars.forEach(key => {
        if (themeColors[key] !== undefined) {
          const base = themeColors[key]

          if (!base) return

          // console.log('setting custom color to', key, themeColors[key])

          // base szín
          document.documentElement.style.setProperty(
            `--v-${key}-base`,
            base
          )

          // hover (világosabb)
          const hover = this.adjustColor(base, 10)

          document.documentElement.style.setProperty(
            `--v-${key}-hover`,
            hover
          )
        }
      })
    },

    setThemeImpl(theme) {
      const name = theme.name
      const light = theme.colors
      // set themes
      Object.keys(light).forEach(i => {
        this.$vuetify.theme.themes.light[i] = light[i]
      })
      const customVars = ['baBannerColor', 'baBannerButtonTextColor', 'modalButtonColor']
      this.updateCustomCssVars(light, customVars)
      // also save theme name to disable selection
      this.$vuetify.theme.themes.name = name
    }
  }
})

moment.defaultFormat = 'YYYY.MM.DD, HH:mm:ss'
let app
let initLocaleCalled = false

async function main() {
  const auth = await createAuth()
  Vue.prototype.$auth = auth
  const authUser = await auth.getUser()
  const externalProviderSettings = {
    apiBaseUrl: store.state.externalProvider?.apiBaseUrl,
    providerId: store.state.externalProvider?.providerId,
    authority: store.state.externalProvider?.authority,
    client_id: store.state.externalProvider?.client_id,
    scope: store.state.externalProvider?.scope,
    prompt: store.state.externalProvider?.prompt
  }
  if (store.state.externalProvider?.authorizationEndpoint && store.state.externalProvider?.tokenEndpoint && store.state.externalProvider?.revocationEndpoint) {
    externalProviderSettings.metadata = {
      authorization_endpoint: store.state.externalProvider.authorizationEndpoint,
      token_endpoint: store.state.externalProvider.token_endpoint,
      revocation_endpoint: store.state.externalProvider.revocationEndpoint
    }
  }
  // console.log('main.js externalProviderSettings', externalProviderSettings)
  // const externalProviderSettings = store.state.externalProvider
  const popupAuth = await createPopupAuth(externalProviderSettings)
  Vue.prototype.$popupAuth = popupAuth

  const remoteConfig = await server.getRemoteConfig()
  store.commit('setRemoteConfig', remoteConfig)

  const HOTJAR_SITEID = store.state.remoteConfig.hotjarSiteId
  if (HOTJAR_SITEID && (HOTJAR_SITEID.length > 0)) {
    Vue.use(Hotjar, {
      id: HOTJAR_SITEID // Hotjar Site ID
    })
  }

  if ((store.state.remoteConfig.theme) && (store.state.remoteConfig.theme.colors)) {
    const dynamicTheme = createDynamicTheme(store.state.remoteConfig.theme)

    store.commit('setCurrentTheme', dynamicTheme)
  }

  if (authUser) {
    let currentUser = { roles: [''] }
    try {
      currentUser = await users.joinUser()
      i18n.locale = currentUser.locale ? currentUser.locale.toLowerCase() : 'en'
      store.commit('setCurrentUser', currentUser)
    } catch (error) {
      console.error('joinUser error', error)
      const errorcode = error.response?.data?.code?.toUpperCase()
      if (errorcode === RdssError.USER_SUSPENDED) {
        const suspendedUser = {
          status: 'SUSPENDED'
        }
        store.commit('setCurrentUser', suspendedUser)
      } else {
        store.commit('removeCurrentUser')
      }
    }

    try {
      await rsspUtil.joinUserOnRssp()
    } catch (error) {
      console.error('rsspUtil.joinUserOnRssp error', error)
    }
  }

  auth.addUserLoaded(initLocale)
  auth.addUserUnloaded(userUnloaded)
  auth.addUserUnloaded(() => {
    popupAuth.logout()
  })

  popupAuth.addUserLoaded(userLoadedExternalProvider)
  popupAuth.addUserUnloaded(userUnloadedExternalProvider)

  const router = createRouter(auth, popupAuth)
  if (store.state.currentUser?.status === 'SUSPENDED') {
    router.push({ name: 'PermissionDenied' })
  } else {
    router.onReady(initEventSource)
  }

  app = new Vue({
    router,
    store,
    vuetify,
    i18n,
    render: h => h(App)
  }).$mount('#app')
}

main()

let sse

async function initLocale() {
  if (initLocaleCalled) return
  initLocaleCalled = true

  const authUser = await Vue.prototype.$auth.getUser()
  if (!authUser) return

  try {
    if (!authUser.profile.locale) {
      const user = await users.joinUser()
      if (user) {
        authUser.profile.locale = user.locale
        store.commit('setCurrentUser', user)
      }
    }

    if (!store.state.currentUser) {
      const currentUser = await users.joinUser()
      store.commit('setCurrentUser', currentUser)
    }

    const currentUser = store.state.currentUser
    const resolvedLocale = (currentUser?.locale || authUser.profile.locale || 'en').toLowerCase()

    if (i18n) i18n.locale = resolvedLocale

    if (app) {
      app.setLanguage(resolvedLocale, false)
    } else {
      vuetify.framework.lang.current = resolvedLocale
    }
  } catch (e) {
    console.error('initLocale error', e)
  }
}

async function userUnloaded() {
  store.commit('removeCurrentUser')
}

async function userLoadedExternalProvider() {
  const authUser = await Vue.prototype.$popupAuth.getUser()
  if (authUser) {
    const settings = {
      apiBaseUrl: Vue.prototype.$popupAuth.settings.apiBaseUrl,
      providerId: Vue.prototype.$popupAuth.settings.providerId,
      authority: Vue.prototype.$popupAuth.settings.authority,
      client_id: Vue.prototype.$popupAuth.settings.client_id,
      scope: Vue.prototype.$popupAuth.settings.scope,
      prompt: Vue.prototype.$popupAuth.settings.prompt,
      authorization_endpoint: Vue.prototype.$popupAuth.settings.metadata?.authorization_endpoint,
      token_endpoint: Vue.prototype.$popupAuth.settings.metadata?.token_endpoint,
      revocation_endpoint: Vue.prototype.$popupAuth.settings.metadata?.revocation_endpoint
    }
    store.commit('setExternalProviderDetails', settings)
  }
}

async function userUnloadedExternalProvider() {
  store.commit('removeExternalProviderDetails')
}

async function initEventSource() {
  const token = await Vue.prototype.$auth.getIdToken()

  const eventSourceURL = store.state.remoteConfig.rdssApiUrl + '/v1/events'

  try {
    // eslint-disable-next-line
    sse = new EventSourcePolyfill(eventSourceURL, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
  } catch (e) {
    console.error(e)
  }

  sse.onopen = (e) => {
    eventBus.$emit(eventTypes.CONNECTION_ESTABLISHED)
  }

  sse.onerror = (error) => {
    eventBus.$emit(eventTypes.CONNECTION_LOST, error)
  }

  sse.addEventListener(eventTypes.HEARTBEAT, (event) => {
    eventBus.$emit(eventTypes.HEARTBEAT)
  })
  sse.addEventListener(eventTypes.SIGNATURE_CHAIN_CREATED, (event) => {
    eventBus.$emit(eventTypes.SIGNATURE_CHAIN_CREATED, JSON.parse(event.data))
  })
  sse.addEventListener(eventTypes.SIGNATURE_CHAIN_UPDATED, (event) => {
    eventBus.$emit(eventTypes.SIGNATURE_CHAIN_UPDATED, JSON.parse(event.data))
  })
  sse.addEventListener(eventTypes.SIGNATURE_CHAIN_DELETED, (event) => {
    eventBus.$emit(eventTypes.SIGNATURE_CHAIN_DELETED, JSON.parse(event.data))
  })
  sse.addEventListener(eventTypes.SIGNATURE_CHAIN_WITHDRAWN, (event) => {
    eventBus.$emit(eventTypes.SIGNATURE_CHAIN_WITHDRAWN, JSON.parse(event.data))
  })

  sse.addEventListener(eventTypes.LOGOUT, (event) => {
    eventBus.$emit(eventTypes.LOGOUT, JSON.parse(event.data))
  })
}
